import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import Error from '$components/Error';
import PageContainer from '$components/PageContainer';
import AssignmentDetails from '$components/AssignmentDetails';
import Grades from '$components/Grades';
import Spinner from '$components/Spinner';
import { MediumUp, SmallOnly } from '$components/Responsive';
import { providers, EDUCATOR } from '$variables/constants';
import ContextBar from '$components/ContextBar';
import ReleaseGradesButton from '$components/ReleaseGradesButton';
import AutosaveStates from '$components/AutosaveStates';
import checkGradesReleased from '$utils/checkGradesReleased';
import {
  Assignment as AssignmentType,
  ErrorType,
  GradeType,
  ViewAs,
} from '$variables/types';
import * as TYPES from '$variables/types';

const AssignmentPage = ({
  spaceId,
  assignment,
  fetchAssignment,
  ltiProviders,
  fetchLtiProviders,
  fetchGrades,
  assignmentLoading,
  gradesLoading,
  error,
  grades,
  launchTurnitinAssignment,
  launchTurnitinSubmission,
  updateAssignment,
  isUpdatingAssignment,
  deleteAssignment,
  gradeManualAssignment,
  // release grades
  currentGrades,
  totalGrades,
  releaseGrades,
  isReleasingGrades,
  // manual assignment grading
  isSavingManualAssignmentGrade,
  lastSavedAtGradeManualAssignment,
  saveManualGradeErrors,
  importManualAssignmentGrades,
  isUploadingGrades,
  viewAs,
}) => {
  const { assignmentId } = useParams();

  const isLoading = assignmentLoading || !assignment;

  useEffect(() => {
    fetchAssignment({ assignmentId });
    fetchGrades({ assignmentId });
    fetchLtiProviders();
  }, [assignmentId]);

  if (error) {
    return <Error hasSideBar={false} />;
  }

  if (isLoading) {
    return (
      <PageContainer hasSideBar={false}>
        <Spinner />
      </PageContainer>
    );
  }

  const anySaveErrors = Object.keys(saveManualGradeErrors).length > 0;

  const RightComponent =
    assignment.provider === providers.MANUAL ? (
      <>
        <MediumUp>
          <AutosaveStates
            isSaving={isSavingManualAssignmentGrade}
            lastSavedAt={lastSavedAtGradeManualAssignment}
            error={anySaveErrors}
          />
        </MediumUp>
        <ReleaseGradesButton
          handleReleaseGrades={() => releaseGrades({ assignmentId })}
          isGradesReleased={!!assignment.gradesReleasedAt}
          currentGrades={currentGrades}
          totalGrades={totalGrades}
          disabled={isReleasingGrades || viewAs !== EDUCATOR}
        />
      </>
    ) : null;

  const BottomComponent =
    assignment.provider === providers.MANUAL ? (
      <SmallOnly>
        <AutosaveStates
          isSaving={isSavingManualAssignmentGrade}
          lastSavedAt={lastSavedAtGradeManualAssignment}
          error={anySaveErrors}
        />
      </SmallOnly>
    ) : null;

  return (
    <>
      <ContextBar
        prevLocation={`/${spaceId}`}
        prevPageTitle="All assignments"
        RightComponent={RightComponent}
        BottomComponent={BottomComponent}
      />
      <PageContainer
        hasSideBar={false}
        rerenderBy={isSavingManualAssignmentGrade}
      >
        <AssignmentDetails
          importManualAssignmentGrades={importManualAssignmentGrades}
          launchTurnitinAssignment={launchTurnitinAssignment}
          launchTurnitinSubmission={launchTurnitinSubmission}
          isUpdatingAssignment={isUpdatingAssignment}
          updateAssignment={updateAssignment}
          deleteAssignment={deleteAssignment}
          assignment={assignment}
          spaceId={spaceId}
          grades={grades}
          isUploadingGrades={isUploadingGrades}
          viewAs={viewAs}
          ltiProvider={ltiProviders[assignment.providerId]}
        />
        <Grades
          isLoading={gradesLoading}
          grades={grades}
          externalId={assignment.externalId}
          assignmentId={assignment.id}
          gradeType={assignment.gradeType}
          editable={
            assignment.provider === providers.MANUAL && viewAs === EDUCATOR
          }
          onEditGrade={gradeManualAssignment}
          spaceId={spaceId}
          hasExternalLink={assignment.provider === providers.HAND_IN}
          disabledText={
            assignment.isAnonymised
              ? 'Link to submission is unavailable for anonymous assignments.'
              : null
          }
          gradesHaveBeenReleased={checkGradesReleased(assignment)}
          saveManualGradeErrors={saveManualGradeErrors}
          provider={assignment.provider}
        />
      </PageContainer>
    </>
  );
};

AssignmentPage.propTypes = {
  assignment: AssignmentType,
  spaceId: PropTypes.string.isRequired,
  fetchAssignment: PropTypes.func.isRequired,
  fetchGrades: PropTypes.func.isRequired,
  assignmentLoading: PropTypes.bool.isRequired,
  error: ErrorType,
  grades: PropTypes.arrayOf(GradeType),
  gradesLoading: PropTypes.bool.isRequired,
  launchTurnitinAssignment: PropTypes.func.isRequired,
  launchTurnitinSubmission: PropTypes.func.isRequired,
  updateAssignment: PropTypes.func.isRequired,
  isUpdatingAssignment: PropTypes.bool.isRequired,
  deleteAssignment: PropTypes.func.isRequired,
  gradeManualAssignment: PropTypes.func.isRequired,
  // release grades
  currentGrades: PropTypes.string.isRequired,
  totalGrades: PropTypes.string.isRequired,
  releaseGrades: PropTypes.func.isRequired,
  isReleasingGrades: PropTypes.bool.isRequired,
  // manual assignment grading
  isSavingManualAssignmentGrade: PropTypes.bool.isRequired,
  lastSavedAtGradeManualAssignment: PropTypes.instanceOf(Date),
  saveManualGradeErrors: PropTypes.objectOf(ErrorType),
  importManualAssignmentGrades: PropTypes.func,
  isUploadingGrades: PropTypes.bool,
  viewAs: ViewAs,
  ltiProviders: PropTypes.arrayOf(TYPES.LtiProvider),
  fetchLtiProviders: PropTypes.func.isRequired,
};

AssignmentPage.defaultProps = {
  error: null,
  assignment: null,
  grades: [],
  lastSavedAtGradeManualAssignment: null,
  saveManualGradeErrors: {},
  importManualAssignmentGrades: () => {},
  isUploadingGrades: false,
  viewAs: EDUCATOR,
  ltiProviders: [],
};

export default AssignmentPage;
